import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Text } from 'reaxl';
import { EmailCTA, OneClickButton } from 'reaxl-email';
import { OwnerWebsiteCTA } from 'reaxl-molecules';

import OwnerWebsiteHandler from '@/utilities/OwnerWebsiteHandler';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import LeadBox from '@/components/vehicledetails/LeadBox';

function PrimaryVdpCallsToAction({
    emailCTAProps,
    disableContactCTAs = false,
    oneClickCTAProps = {},
    phoneAndTextHandler,
}) {

    const {
        handleSendOneClickEmail,
        oneClickEmailSent,
    } = oneClickCTAProps;

    const device = useDevice();
    const isXs = device?.is?.xs ?? false;

    const {
        esntial_confident_purchase: [isEsntialConfidentPurchase],
        one_click_lead_vdp: [enableOneClickVDP],
        thirdparty_prequal: [, {
            enable_email_at_xs: isXsEmailCtaEnabled,
        }],
    } = useFeatures([
        'esntial_confident_purchase',
        'one_click_lead_vdp',
        'thirdparty_prequal',
    ]);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const {
        esntial,
        id: listingId,
        owner = {},
        ecommWebsite = {},
        vin,
    } = listing;

    const { hideEmailForm, dealerPreferredCTA, website = {}, id: ownerId, privateSeller: isPrivateSeller } = owner;
    const existingDeal = useSelector((state) => paymentsDuck?.selectors?.getDealByListingVin(state, vin)) || {};
    const leadProfile = useSelector((state) => userDuck?.selectors?.getLeadProfile(state));

    const showOneClickCTA = enableOneClickVDP && leadProfile?.isOneClickOptedIn;
    const showEcommCTA = Object.keys(ecommWebsite).length > 1;
    const { label = 'Visit Dealer Website', href = '' } = showEcommCTA ? ecommWebsite : website;
    const ecommHrefWithWalletData = useSelector((state) => vdpResultsDuck.selectors.getEcommHrefWithWalletData(state), _isEqual);
    const ecommLink = !!ecommWebsite?.href;
    const showWebsiteButton = !(isEsntialConfidentPurchase && esntial) && dealerPreferredCTA === 'WEBSITE' && !!href;
    const analyticsData = {
        inventoryId: listingId,
        ownerId,
        placement: '',
        ecommLink,
        ctaLocation: 'primary-vdp-cta',
        label,
    };

    const ownerCtaClassNames = 'margin-bottom-3 margin-bottom-sm-5';

    let primaryCta = null;

    if (showWebsiteButton && !existingDeal?.isEcommerce) {
        primaryCta = (
            <>
                {!!esntial && !isXs && (
                    <Text
                        className="padding-top-1 padding-bottom-3"
                        weight="bold"
                        componentClass="div"
                    >
                        Buy this car from the comfort of your own home including stress-free pick up or delivery!
                    </Text>
                )}
                <OwnerWebsiteCTA
                    href={href}
                    onClick={OwnerWebsiteHandler({ ecommHref: ecommHrefWithWalletData, openInSameTab: !!esntial })}
                    uiContext="block"
                    label={label}
                    hideIcon={esntial && showEcommCTA}
                    analyticsData={analyticsData}
                    className={ownerCtaClassNames}
                />
            </>
        );
    } else if (Object.keys(emailCTAProps).length && (isXsEmailCtaEnabled && isXs) && !(isPrivateSeller || hideEmailForm)) {
        primaryCta = (
            <>
                {!showOneClickCTA && (
                    <EmailCTA
                        {...emailCTAProps}
                        buttonStyle={dealerPreferredCTA === 'WEBSITE' && isXs ? 'default' : emailCTAProps?.buttonStyle}
                        type="block"
                        context="confirmAvailability"
                        labelKey="checkAvailability"
                        parentId="price-card"
                        className={ownerCtaClassNames}
                    />
                )}
                {showOneClickCTA && (
                    <OneClickButton
                        handleEmailSendClick={handleSendOneClickEmail}
                        hideModifySettings
                        isFullSize
                        isModifySettingsIcon={false}
                        oneClickEmailSent={oneClickEmailSent}
                        wrapperClassNames="margin-bottom-5"
                    />
                )}
            </>
        );
    }

    return !!primaryCta && (
        isXs
            ? (
                <LeadBox
                    disableContactCTAs={disableContactCTAs}
                    key="lead_box"
                    phoneAndTextHandler={phoneAndTextHandler}
                >
                    {primaryCta}
                </LeadBox>
            ) : primaryCta
    );

}

PrimaryVdpCallsToAction.propTypes = {
    /**
     * Boolean to disable phone/chat/email for the Hyundai experience
     */
    disableContactCTAs: PropTypes.bool,
    /**
     * the properties for EmailCTA
     */
    emailCTAProps: PropTypes.object,
    /**
     * Properties for OneClickCTA functionality
     */
    oneClickCTAProps: PropTypes.object,
    /**
     * Handler function for phone and text interactions
     */
    phoneAndTextHandler: PropTypes.func,
};

export const PrimaryVdpCallsToActionContainer = memo(PrimaryVdpCallsToAction);

export default PrimaryVdpCallsToActionContainer;
